<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Collapsible or tabbed rich-content panels. Distinct from `faq`: panel bodies
 * hold HTML rather than plain answers, and tabs mode shows one panel at a time
 * with a persistent tab strip.
 *
 * Panel HTML is purified server-side at save time (GenericBlocks::HTML_FIELDS),
 * so v-html is safe to SSR here — same contract as rich_text / two_column.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const panels = computed(() => props.data.panels ?? [])
const isTabs = computed(() => props.data.display === 'tabs')

// Tabs: one always open, defaulting to the first. Accordion: all closed.
const active = ref(0)
const open = ref<number | null>(null)
const toggle = (i: number) => {
  open.value = open.value === i ? null : i
}

const proseClass
  = 'site-prose leading-relaxed [&_a]:underline [&_h2]:site-display [&_h3]:site-display [&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-4 [&_blockquote]:italic'
</script>

<template>
  <div>
    <h3
      v-if="data.headline"
      class="site-display mb-6 text-center text-2xl tracking-tight sm:text-3xl"
    >
      {{ data.headline }}
    </h3>

    <!-- Tabs -->
    <div v-if="panels.length && isTabs">
      <div
        role="tablist"
        class="site-scroll-x mb-5 flex gap-1 border-b border-[var(--site-border)]"
      >
        <button
          v-for="(panel, i) in panels"
          :key="i"
          type="button"
          role="tab"
          :aria-selected="active === i"
          class="-mb-px shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition"
          :class="active === i
            ? 'site-text-accent border-[var(--site-accent)]'
            : 'border-transparent text-[var(--site-muted)]'"
          @click="active = i"
        >
          {{ panel.title }}
        </button>
      </div>
      <div
        v-for="(panel, i) in panels"
        v-show="active === i"
        :key="i"
        role="tabpanel"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="panel.html"
          :class="proseClass"
          v-html="panel.html"
        />
      </div>
    </div>

    <!-- Accordion -->
    <div
      v-else-if="panels.length"
      class="mx-auto max-w-2xl divide-y divide-[var(--site-border)]"
    >
      <div
        v-for="(panel, i) in panels"
        :key="i"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between gap-4 py-4 text-left"
          :aria-expanded="open === i"
          @click="toggle(i)"
        >
          <span class="font-semibold">{{ panel.title }}</span>
          <svg
            class="size-4 shrink-0 text-[var(--site-muted)] transition-transform"
            :class="open === i ? 'rotate-180' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              d="m6 9 6 6 6-6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="open === i && panel.html"
          class="pb-4"
          :class="proseClass"
          v-html="panel.html"
        />
      </div>
    </div>
  </div>
</template>
